import { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { contactsApi, faxReceivedApi, doNotCallApi } from "@/api";
import { format } from "date-fns";
import {
  ArrowLeft, Mail, Building, User, Star, Globe, Phone,
  MessageSquare, MessagesSquare, Check, FileText,
  ChevronDown, ChevronRight, GitBranch, PhoneOff,
} from "lucide-react";
import type { ContactStatus, Source, TimelineChannel, TimelineItem, LineMessage } from "@/types";

const sourceLabels: Record<Source, string> = {
  TRADER: "取引先",
  TRADER_CONTACT: "担当者",
  MEMBER: "会員",
  FAX_PROSPECT: "FAXリスト",
};

const statusLabels: Record<ContactStatus, string> = {
  ACTIVE: "有効",
  UNSUBSCRIBED: "配信停止",
  BOUNCED: "バウンス",
};

const statusColors: Record<ContactStatus, string> = {
  ACTIVE: "bg-green-100 text-green-700",
  UNSUBSCRIBED: "bg-slate-100 text-slate-600",
  BOUNCED: "bg-red-100 text-red-700",
};

// --- Timeline channel config ---

const CHANNEL_FILTERS: { key: TimelineChannel | "ALL"; label: string }[] = [
  { key: "ALL", label: "全て" },
  { key: "EMAIL", label: "メール" },
  { key: "SMS", label: "SMS" },
  { key: "LINE", label: "LINE" },
  { key: "FAX", label: "FAX" },
  { key: "CALL", label: "架電" },
  { key: "WEB", label: "Web" },
  { key: "SEQUENCE", label: "シーケンス" },
];

const CHANNEL_ICON: Record<TimelineChannel, { icon: typeof Mail; color: string }> = {
  EMAIL: { icon: Mail, color: "text-blue-500" },
  SMS: { icon: MessageSquare, color: "text-green-500" },
  LINE: { icon: MessagesSquare, color: "text-[#06C755]" },
  FAX: { icon: FileText, color: "text-orange-500" },
  CALL: { icon: Phone, color: "text-purple-500" },
  WEB: { icon: Globe, color: "text-slate-500" },
  SEQUENCE: { icon: GitBranch, color: "text-indigo-500" },
};

const CHANNEL_BG: Record<TimelineChannel, string> = {
  EMAIL: "bg-blue-50",
  SMS: "bg-green-50",
  LINE: "bg-emerald-50",
  FAX: "bg-orange-50",
  CALL: "bg-purple-50",
  WEB: "bg-slate-50",
  SEQUENCE: "bg-indigo-50",
};

// --- Timeline item detail renderers ---

function CampaignSendDetail({ item }: { item: TimelineItem }) {
  const m = item.metadata;
  return (
    <div className="text-xs space-y-1 text-slate-600">
      {m.campaignName && <p>キャンペーン: <span className="font-medium">{m.campaignName as string}</span></p>}
      {m.subject && <p>件名: {m.subject as string}</p>}
      {m.openedAt && <p>開封: {format(new Date(m.openedAt as string), "MM/dd HH:mm")}</p>}
      {m.clickedAt && <p>クリック: {format(new Date(m.clickedAt as string), "MM/dd HH:mm")}</p>}
    </div>
  );
}

function EngagementDetail({ item }: { item: TimelineItem }) {
  const m = item.metadata;
  return (
    <div className="text-xs space-y-1 text-slate-600">
      <p>イベント: <span className="font-medium">{m.eventType as string}</span></p>
      {m.url && (
        <p>URL: <a href={m.url as string} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{m.url as string}</a></p>
      )}
    </div>
  );
}

function CallDetail({ item }: { item: TimelineItem }) {
  const m = item.metadata;
  const transcript = m.transcript as Array<{ turn: number; role: string; text: string }> | null;
  return (
    <div className="text-xs space-y-1 text-slate-600">
      {m.campaignName && <p>キャンペーン: <span className="font-medium">{m.campaignName as string}</span></p>}
      <div className="flex gap-3">
        {m.outcome && <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">{m.outcome as string}</span>}
        {(m.duration as number) > 0 && <span>通話時間: {m.duration as number}秒</span>}
        {(m.turns as number) > 0 && <span>ターン: {m.turns as number}</span>}
      </div>
      {transcript && transcript.length > 0 && (
        <div className="mt-2 max-h-40 overflow-y-auto border rounded p-2 bg-white space-y-1">
          {transcript.map((t, i) => (
            <p key={i} className={t.role === "assistant" ? "text-purple-600" : "text-slate-700"}>
              <span className="font-medium">{t.role === "assistant" ? "AI" : "相手"}:</span> {t.text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function FaxDetail({ item }: { item: TimelineItem }) {
  const m = item.metadata;
  return (
    <div className="text-xs space-y-1 text-slate-600">
      <p>送信元: <span className="font-mono">{m.senderNumber as string}</span></p>
      {m.pageCount && <p>ページ数: {m.pageCount as number}</p>}
      {m.faxType && <p>種別: {m.faxType as string}</p>}
      {m.ocrSummary && <p>OCR: {m.ocrSummary as string}</p>}
      {m.faxId && m.documentId && (
        <a
          href={faxReceivedApi.pdfUrl(m.faxId as string)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline inline-block mt-1"
        >
          PDF表示
        </a>
      )}
    </div>
  );
}

function WebEventDetail({ item }: { item: TimelineItem }) {
  const m = item.metadata;
  return (
    <div className="text-xs space-y-1 text-slate-600">
      {m.url && (
        <p>URL: <a href={m.url as string} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all">{m.url as string}</a></p>
      )}
      {m.title && <p>タイトル: {m.title as string}</p>}
      {m.referrer && <p>リファラー: {m.referrer as string}</p>}
      {(m.score as number) > 0 && <p>スコア: <span className="font-bold text-orange-600">+{m.score as number}</span></p>}
    </div>
  );
}

function SequenceDetail({ item }: { item: TimelineItem }) {
  const m = item.metadata;
  return (
    <div className="text-xs space-y-1 text-slate-600">
      {m.sequenceName && <p>シーケンス: <span className="font-medium">{m.sequenceName as string}</span></p>}
      <p>ステップ: {m.currentStep as number}</p>
      {m.enrollmentStatus && <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">{m.enrollmentStatus as string}</span>}
      {m.exitReason && <p>退出理由: {m.exitReason as string}</p>}
    </div>
  );
}

function LineMessageDetail({ item }: { item: TimelineItem }) {
  const m = item.metadata;
  const isIncoming = m.direction === "INCOMING";
  return (
    <div className="text-xs space-y-1 text-slate-600">
      <p>{isIncoming ? "受信" : "送信"} ({m.messageType as string})</p>
      {m.body && <p className="bg-white border rounded p-2">{m.body as string}</p>}
    </div>
  );
}

const DETAIL_RENDERER: Record<string, React.FC<{ item: TimelineItem }>> = {
  CAMPAIGN_SEND: CampaignSendDetail,
  ENGAGEMENT: EngagementDetail,
  CALL: CallDetail,
  FAX_RECEIVED: FaxDetail,
  WEB_EVENT: WebEventDetail,
  SEQUENCE: SequenceDetail,
  LINE_MESSAGE: LineMessageDetail,
};

// --- Timeline item row ---

function TimelineRow({ item }: { item: TimelineItem }) {
  const [expanded, setExpanded] = useState(false);
  const channelCfg = CHANNEL_ICON[item.channel];
  const Icon = channelCfg.icon;
  const DetailRenderer = DETAIL_RENDERER[item.type];

  return (
    <div className={`border-b last:border-b-0 ${CHANNEL_BG[item.channel]}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-white/50 transition-colors"
      >
        <div className="mt-0.5 flex-shrink-0">
          <Icon size={16} className={channelCfg.color} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-800 truncate">{item.summary}</p>
          {item.status && (
            <span className="text-xs text-slate-500">{item.status}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-slate-400 whitespace-nowrap">
            {format(new Date(item.timestamp), "MM/dd HH:mm")}
          </span>
          {expanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        </div>
      </button>
      {expanded && DetailRenderer && (
        <div className="px-4 pb-3 pl-11">
          <DetailRenderer item={item} />
        </div>
      )}
    </div>
  );
}

// --- Main component ---

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [lineUserIdInput, setLineUserIdInput] = useState("");
  const [showLineInput, setShowLineInput] = useState(false);
  const [activeChannels, setActiveChannels] = useState<TimelineChannel[]>([]);

  const { data: contact, isLoading } = useQuery({
    queryKey: ["contact", id],
    queryFn: () => contactsApi.get(id!),
    enabled: !!id,
  });

  const patchMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => contactsApi.patch(id!, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contact", id] }),
  });

  const doNotCallMutation = useMutation({
    mutationFn: () =>
      contact?.doNotCall
        ? doNotCallApi.remove(id!)
        : doNotCallApi.add(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact", id] });
      queryClient.invalidateQueries({ queryKey: ["do-not-call"] });
    },
  });

  const toggleChannel = useCallback((ch: TimelineChannel | "ALL") => {
    if (ch === "ALL") {
      setActiveChannels([]);
    } else {
      setActiveChannels((prev) => {
        if (prev.includes(ch)) {
          return prev.filter((c) => c !== ch);
        }
        return [...prev, ch];
      });
    }
  }, []);

  const {
    data: timelineData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isTimelineLoading,
  } = useInfiniteQuery({
    queryKey: ["contact-timeline", id, activeChannels],
    queryFn: ({ pageParam }) =>
      contactsApi.timeline(id!, {
        cursor: pageParam as string | undefined,
        limit: 30,
        channels: activeChannels.length ? activeChannels : undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor ?? undefined : undefined,
    enabled: !!id,
  });

  const timelineItems = timelineData?.pages.flatMap((p) => p.data) ?? [];

  if (isLoading) return <p className="text-slate-500">読み込み中...</p>;
  if (!contact) return <p className="text-slate-500">コンタクトが見つかりません</p>;

  const name = [contact.lastName, contact.firstName].filter(Boolean).join(" ") || "-";

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate("/contacts")} className="text-slate-400 hover:text-slate-600">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold">{contact.email}</h2>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[contact.status]}`}>
              {statusLabels[contact.status]}
            </span>
            {contact.doNotCall && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                <PhoneOff size={12} />
                架電NG
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Contact info */}
      <div className="bg-white p-6 rounded-lg shadow-sm border grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="flex items-start gap-2">
          <User size={16} className="text-slate-400 mt-0.5" />
          <div>
            <p className="text-xs text-slate-500">名前</p>
            <p className="text-sm font-medium">{name}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Building size={16} className="text-slate-400 mt-0.5" />
          <div>
            <p className="text-xs text-slate-500">会社名</p>
            <p className="text-sm font-medium">{contact.company || "-"}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Mail size={16} className="text-slate-400 mt-0.5" />
          <div>
            <p className="text-xs text-slate-500">ソース</p>
            <p className="text-sm font-medium">{sourceLabels[contact.source]}</p>
          </div>
        </div>
        {contact.phone && (
          <div className="flex items-start gap-2">
            <Phone size={16} className="text-slate-400 mt-0.5" />
            <div>
              <p className="text-xs text-slate-500">電話番号</p>
              <p className="text-sm font-medium">{contact.phone}</p>
            </div>
          </div>
        )}
        {contact.faxNumber && (
          <div className="flex items-start gap-2">
            <FileText size={16} className="text-orange-400 mt-0.5" />
            <div>
              <p className="text-xs text-slate-500">FAX番号</p>
              <p className="text-sm font-medium font-mono">{contact.faxNumber}</p>
            </div>
          </div>
        )}
        <div className="flex items-start gap-2">
          <Star size={16} className="text-slate-400 mt-0.5" />
          <div>
            <p className="text-xs text-slate-500">リードスコア</p>
            <p className="text-sm font-bold text-blue-600">{contact.leadScore}</p>
          </div>
        </div>
        <div>
          <p className="text-xs text-slate-500">取引先ランク</p>
          <p className="text-sm font-medium">{contact.traderRank || "-"}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">RFMランク</p>
          <p className="text-sm font-medium">{contact.rfmRank || "-"}</p>
        </div>
        {contact.traderType && (
          <div>
            <p className="text-xs text-slate-500">取引先タイプ</p>
            <p className="text-sm font-medium">{contact.traderType}</p>
          </div>
        )}
        {contact.cpsScore != null && (
          <div>
            <p className="text-xs text-slate-500">CPSスコア</p>
            <p className="text-sm font-medium">{contact.cpsScore}</p>
          </div>
        )}
        {contact.lastOrderAt && (
          <div>
            <p className="text-xs text-slate-500">最終注文</p>
            <p className="text-sm font-medium">{format(new Date(contact.lastOrderAt), "yyyy/MM/dd")}</p>
          </div>
        )}
      </div>

      {/* Channel settings */}
      <div className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
        <h3 className="text-sm font-bold text-slate-700">チャネル設定</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {/* SMS consent */}
          <div className="flex items-center gap-3">
            <MessageSquare size={16} className="text-green-500" />
            <div className="flex-1">
              <p className="text-xs text-slate-500">SMS同意</p>
              <button
                onClick={() => patchMutation.mutate({ smsConsent: !contact.smsConsent })}
                className={`mt-0.5 px-2.5 py-0.5 rounded text-xs font-medium transition-colors ${
                  contact.smsConsent ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                }`}
              >
                {contact.smsConsent ? "同意済み" : "未同意"}
              </button>
            </div>
          </div>

          {/* LINE userId */}
          <div className="flex items-start gap-3">
            <MessagesSquare size={16} className="text-[#06C755] mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-slate-500">LINE userId</p>
              {contact.lineUserId ? (
                <div>
                  <p className="text-xs font-mono text-slate-600 truncate" title={contact.lineUserId}>{contact.lineUserId.slice(0, 16)}...</p>
                  <span className={`text-xs ${contact.lineFollowing ? "text-green-600" : "text-slate-400"}`}>
                    {contact.lineFollowing ? "フォロー中" : "未フォロー"}
                  </span>
                </div>
              ) : showLineInput ? (
                <div className="flex gap-1 mt-0.5">
                  <input
                    value={lineUserIdInput}
                    onChange={(e) => setLineUserIdInput(e.target.value)}
                    placeholder="Uxxxxxxx..."
                    className="border rounded px-2 py-0.5 text-xs w-28"
                  />
                  <button
                    onClick={() => { patchMutation.mutate({ lineUserId: lineUserIdInput, lineFollowing: true }); setShowLineInput(false); }}
                    disabled={!lineUserIdInput.startsWith("U")}
                    className="p-0.5 bg-green-500 text-white rounded disabled:opacity-50"
                  >
                    <Check size={12} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowLineInput(true)}
                  className="mt-0.5 text-xs text-blue-500 hover:text-blue-700"
                >
                  紐付ける
                </button>
              )}
            </div>
          </div>

          {/* Phone */}
          <div className="flex items-start gap-3">
            <Phone size={16} className="text-slate-400 mt-0.5" />
            <div>
              <p className="text-xs text-slate-500">携帯番号</p>
              <p className="text-sm font-medium">{contact.cell || contact.phone || "-"}</p>
            </div>
          </div>

          {/* Do Not Call */}
          <div className="flex items-center gap-3">
            <PhoneOff size={16} className="text-red-400" />
            <div className="flex-1">
              <p className="text-xs text-slate-500">架電NG</p>
              <button
                onClick={() => {
                  if (contact.doNotCall) {
                    if (confirm("架電NGを解除しますか？")) doNotCallMutation.mutate();
                  } else {
                    if (confirm("このコンタクトを架電NGに登録しますか？")) doNotCallMutation.mutate();
                  }
                }}
                disabled={doNotCallMutation.isPending}
                className={`mt-0.5 px-2.5 py-0.5 rounded text-xs font-medium transition-colors ${
                  contact.doNotCall ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"
                }`}
              >
                {contact.doNotCall ? "NG登録済み" : "未登録"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* LINE Chat */}
      {contact.lineUserId && <LineChatPanel contactId={id!} />}

      {/* Unified Timeline */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50">
          <h3 className="text-sm font-bold mb-2">タイムライン</h3>
          <div className="flex flex-wrap gap-1.5">
            {CHANNEL_FILTERS.map((f) => {
              const isActive = f.key === "ALL" ? activeChannels.length === 0 : activeChannels.includes(f.key as TimelineChannel);
              return (
                <button
                  key={f.key}
                  onClick={() => toggleChannel(f.key as TimelineChannel | "ALL")}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    isActive
                      ? "bg-slate-700 text-white"
                      : "bg-white text-slate-500 border hover:bg-slate-100"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {isTimelineLoading ? (
          <p className="px-4 py-8 text-center text-slate-400 text-sm">読み込み中...</p>
        ) : timelineItems.length === 0 ? (
          <p className="px-4 py-8 text-center text-slate-400 text-sm">タイムラインデータがありません</p>
        ) : (
          <>
            {timelineItems.map((item) => (
              <TimelineRow key={item.id} item={item} />
            ))}
            {hasNextPage && (
              <div className="px-4 py-3 text-center border-t">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="text-sm text-blue-500 hover:text-blue-700 disabled:text-slate-400"
                >
                  {isFetchingNextPage ? "読み込み中..." : "さらに読み込む"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// --- LINE Chat Panel ---

function LineChatPanel({ contactId }: { contactId: string }) {
  const { data } = useQuery({
    queryKey: ["line-messages", contactId],
    queryFn: () => contactsApi.lineMessages(contactId, { limit: "50" }),
  });

  const messages = data?.data ?? [];

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      <div className="px-4 py-3 border-b bg-[#06C755]/10 flex items-center gap-2">
        <MessagesSquare size={16} className="text-[#06C755]" />
        <h3 className="text-sm font-bold text-slate-700">LINEチャット</h3>
        <span className="text-xs text-slate-400 ml-auto">{messages.length}件</span>
      </div>
      {messages.length === 0 ? (
        <p className="px-4 py-6 text-center text-slate-400 text-sm">メッセージなし</p>
      ) : (
        <div className="p-4 space-y-2 max-h-80 overflow-y-auto bg-slate-50">
          {messages.map((msg: LineMessage) => {
            const isIncoming = msg.direction === "INCOMING";
            return (
              <div key={msg.id} className={`flex ${isIncoming ? "" : "justify-end"}`}>
                <div className={`max-w-[70%] px-3 py-2 rounded-2xl text-sm ${
                  isIncoming
                    ? "bg-white border text-slate-700 rounded-bl-sm"
                    : "bg-[#06C755] text-white rounded-br-sm"
                }`}>
                  {msg.body || `[${msg.messageType}]`}
                  <div className={`text-[10px] mt-0.5 ${isIncoming ? "text-slate-400" : "text-white/70"}`}>
                    {format(new Date(msg.createdAt), "MM/dd HH:mm")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
